<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SicargRolesAPI from 'dashboard/api/sicargRoles';
import { SICARG_SECTIONS } from 'dashboard/helper/sicargPermissions';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextInput from 'next/input/Input.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// SICARG MOD 5 — Gestión de roles con permisos por sección (admin).
const { t } = useI18n();

const sections = SICARG_SECTIONS;
const roles = ref([]);
const isLoading = ref(false);
const editing = ref(null);

const buildDefaultPermissions = () =>
  sections.reduce((acc, section) => {
    acc[section] = { view: true, edit: true };
    // SICARG — 'edit_existing' es opt-in (default false), solo aplica a 'contacts'.
    if (section === 'contacts') acc[section].edit_existing = false;
    return acc;
  }, {});

const sectionLabel = section =>
  t(`SICARG_ROLES.SECTIONS.${section.toUpperCase()}`, section);

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await SicargRolesAPI.get();
    roles.value = data;
  } catch (error) {
    useAlert(t('SICARG_ROLES.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const newRole = () => {
  editing.value = {
    id: null,
    name: '',
    description: '',
    active: true,
    section_permissions: buildDefaultPermissions(),
  };
};

const editRole = role => {
  const merged = buildDefaultPermissions();
  Object.keys(role.section_permissions || {}).forEach(section => {
    merged[section] = {
      view: role.section_permissions[section].view !== false,
      edit: role.section_permissions[section].edit !== false,
    };
    // SICARG — 'edit_existing' es opt-in: ausente o cualquier valor que no sea
    // true explícito se toma como false (al revés del !== false de arriba).
    if (section === 'contacts') {
      merged[section].edit_existing =
        role.section_permissions[section].edit_existing === true;
    }
  });
  editing.value = {
    id: role.id,
    name: role.name,
    description: role.description || '',
    active: role.active,
    section_permissions: merged,
  };
};

const cancelEdit = () => {
  editing.value = null;
};

const saveRole = async () => {
  if (!editing.value.name.trim()) {
    useAlert(t('SICARG_ROLES.API.NAME_REQUIRED'));
    return;
  }
  isLoading.value = true;
  const payload = {
    name: editing.value.name.trim(),
    description: editing.value.description.trim(),
    active: editing.value.active,
    section_permissions: editing.value.section_permissions,
  };
  try {
    if (editing.value.id) {
      await SicargRolesAPI.update(editing.value.id, { sicarg_role: payload });
    } else {
      await SicargRolesAPI.create({ sicarg_role: payload });
    }
    useAlert(t('SICARG_ROLES.API.SAVE_SUCCESS'));
    editing.value = null;
    await load();
  } catch (error) {
    useAlert(t('SICARG_ROLES.API.SAVE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const deleteRole = async role => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('SICARG_ROLES.DELETE_CONFIRM', { name: role.name })))
    return;
  try {
    await SicargRolesAPI.delete(role.id);
    await load();
    useAlert(t('SICARG_ROLES.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('SICARG_ROLES.API.DELETE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-6">
    <template #header>
      <BaseSettingsHeader
        :title="t('SICARG_ROLES.HEADER.TITLE')"
        :description="t('SICARG_ROLES.HEADER.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            blue
            sm
            icon="i-lucide-plus"
            :label="t('SICARG_ROLES.ADD')"
            @click="newRole"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6 mt-4 max-w-3xl">
        <!-- Editor -->
        <div
          v-if="editing"
          class="flex flex-col gap-4 p-4 rounded-lg border border-n-weak"
        >
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('SICARG_ROLES.FIELDS.NAME') }}
            </label>
            <NextInput
              v-model="editing.name"
              :placeholder="t('SICARG_ROLES.FIELDS.NAME_PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('SICARG_ROLES.FIELDS.DESCRIPTION') }}
            </label>
            <NextInput
              v-model="editing.description"
              :placeholder="t('SICARG_ROLES.FIELDS.DESCRIPTION_PLACEHOLDER')"
            />
          </div>
          <label class="flex gap-2 justify-between items-center">
            <span class="text-sm text-n-slate-11">{{
              t('SICARG_ROLES.FIELDS.ACTIVE')
            }}</span>
            <Switch v-model="editing.active" />
          </label>

          <!-- Matriz de permisos por sección -->
          <div class="flex flex-col gap-2">
            <div
              class="grid grid-cols-3 gap-2 pb-2 text-xs font-medium text-n-slate-11 border-b border-n-weak"
            >
              <span>{{ t('SICARG_ROLES.MATRIX.SECTION') }}</span>
              <span class="text-center">{{ t('SICARG_ROLES.MATRIX.VIEW') }}</span>
              <span class="text-center">{{ t('SICARG_ROLES.MATRIX.EDIT') }}</span>
            </div>
            <div
              v-for="section in sections"
              :key="section"
              class="flex flex-col gap-1 py-1"
            >
              <div class="grid grid-cols-3 gap-2 items-center">
                <span class="text-sm text-n-slate-12">{{
                  sectionLabel(section)
                }}</span>
                <div class="flex justify-center">
                  <Switch v-model="editing.section_permissions[section].view" />
                </div>
                <div class="flex justify-center">
                  <Switch v-model="editing.section_permissions[section].edit" />
                </div>
              </div>
              <!-- SICARG — 'edit_existing' solo tiene sentido para Contactos:
                   habilita editar un contacto ya creado (distinto de 'edit', que
                   controla crear/borrar/importar/exportar en la sección). -->
              <div
                v-if="section === 'contacts'"
                class="grid grid-cols-3 gap-2 items-center pl-4"
              >
                <span class="text-xs text-n-slate-11">{{
                  t('SICARG_ROLES.MATRIX.EDIT_EXISTING_CONTACT')
                }}</span>
                <div />
                <div class="flex justify-center">
                  <Switch
                    v-model="editing.section_permissions.contacts.edit_existing"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="flex gap-2">
            <NextButton blue :is-loading="isLoading" @click="saveRole">
              {{ t('SICARG_ROLES.SAVE') }}
            </NextButton>
            <NextButton faded slate @click="cancelEdit">
              {{ t('SICARG_ROLES.CANCEL') }}
            </NextButton>
          </div>
        </div>

        <!-- Listado -->
        <div class="flex flex-col gap-2">
          <div
            v-for="role in roles"
            :key="role.id"
            class="flex justify-between items-center p-4 rounded-lg border border-n-weak"
          >
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ role.name }}
                <span
                  v-if="!role.active"
                  class="ml-2 text-xs text-n-slate-10"
                >({{ t('SICARG_ROLES.INACTIVE') }})</span>
              </p>
              <p v-if="role.description" class="text-sm text-n-slate-11">
                {{ role.description }}
              </p>
            </div>
            <div class="flex gap-1">
              <NextButton
                ghost
                slate
                xs
                icon="i-lucide-pencil"
                @click="editRole(role)"
              />
              <NextButton
                ghost
                ruby
                xs
                icon="i-lucide-trash-2"
                @click="deleteRole(role)"
              />
            </div>
          </div>
          <p
            v-if="!roles.length && !editing"
            class="text-sm text-n-slate-10"
          >
            {{ t('SICARG_ROLES.EMPTY') }}
          </p>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
